import React, { useCallback, useRef, useState } from 'react';

import { FormattedMessage, useIntl } from 'react-intl-next';

import Button, { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import { Label } from '@atlaskit/form';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import EyeOpenStrikethroughIcon from '@atlaskit/icon/core/eye-open-strikethrough';
import { Popup } from '@atlaskit/popup';
import { Box, Inline, Stack } from '@atlaskit/primitives/compiled';
import TextField from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';
import { CuratedPalette, DEFAULT_GRAY_COLOR, useBuiltInColorMap } from '@atlassian/color-picker';

import { useProductDependencies } from '../../../controllers/use-product-dependencies';
import { PanelSubheader } from '../panel-subheader';

import { LABEL_COLOR_ANALYTIC_SOURCE } from './constants';
import { messages } from './messages';

const styles = cssMap({
	labelPopupContainer: {
		width: '100%',
		paddingTop: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
		paddingRight: token('space.150'),
	},
});

export type LabelButtonProps = {
	handleIsLabelHiddenChange: (value: boolean) => void;
	handleLabelChange: (value: string) => void;
	handleLabelColorChange: (value: string) => void;
	isLabelHiddenValue: boolean | undefined;
	labelColorValue: string | undefined;
	labelId: string;
	labelValue: string | undefined;
};

export const LabelButton = ({
	labelId,
	labelValue,
	handleLabelChange,
	isLabelHiddenValue,
	handleIsLabelHiddenChange,
	labelColorValue,
	handleLabelColorChange,
}: LabelButtonProps): React.JSX.Element => {
	const { isSSRProductInDarkMode, branding } = useProductDependencies();
	const { hideDefaultColorPalette, brandBackgroundColorMap } = branding ?? {};
	const { backgroundColorMap } = useBuiltInColorMap(isSSRProductInDarkMode);

	const [openPopup, setOpenPopup] = useState(false);
	const { formatMessage } = useIntl();

	const customColorRef = useRef<typeof labelColorValue>();

	const defaultLabelColor = DEFAULT_GRAY_COLOR;

	const handleChangeTextField = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			handleLabelChange(event.currentTarget.value.toUpperCase());
		},
		[handleLabelChange],
	);

	const handleChangeColorPicker = useCallback(
		(color: string) => {
			handleLabelColorChange(color);
		},
		[handleLabelColorChange],
	);

	const handleHideToggle = () => {
		handleIsLabelHiddenChange(!isLabelHiddenValue);
	};

	return (
		<Popup
			isOpen={openPopup}
			onClose={() => setOpenPopup(false)}
			placement="bottom-start"
			content={() => (
				<Stack space="space.200" xcss={styles.labelPopupContainer} testId="label-popup-content">
					<Inline spread="space-between">
						<Label htmlFor={labelId}>
							<PanelSubheader>{<FormattedMessage {...messages.labelPopupTitle} />}</PanelSubheader>
						</Label>
						<Box>
							<IconButton
								isTooltipDisabled={false}
								isSelected={isLabelHiddenValue}
								spacing="compact"
								appearance="subtle"
								label={formatMessage(messages.hideLabelButtonText)}
								icon={EyeOpenStrikethroughIcon}
								onClick={handleHideToggle}
							/>
						</Box>
					</Inline>
					<TextField
						id={labelId}
						value={labelValue?.toUpperCase()}
						onChange={handleChangeTextField}
					/>
					<CuratedPalette
						analyticsSource={LABEL_COLOR_ANALYTIC_SOURCE}
						builtInColorMap={
							hideDefaultColorPalette && brandBackgroundColorMap
								? brandBackgroundColorMap
								: backgroundColorMap
						}
						customColorRef={customColorRef}
						onChange={handleChangeColorPicker}
						value={labelColorValue || defaultLabelColor}
					/>
				</Stack>
			)}
			trigger={(triggerProps) => (
				<Button
					{...triggerProps}
					iconAfter={(iconProps) => <ChevronDownIcon {...iconProps} size="small" />}
					onClick={() => setOpenPopup(!openPopup)}
					isSelected={openPopup}
				>
					{formatMessage(messages.labelButtonTitle)}
				</Button>
			)}
		/>
	);
};
